import dayjs from 'dayjs'

/**
 * Time windows ("buckets") of the MAM rollups and the alarm units.
 *
 * The backend stores every bucket under its START (`roundToStartingSecond`,
 * `bucketStart` in hono): sub-day buckets on the local grid (6h → 00/06/12/18
 * local), 24h at local midnight. Charts and the alarm list show a bucket at /
 * up to its END instead — a value only exists once its window is over.
 */

/** Bucket length in seconds; unknown keys → 0 (= no window, a single reading). */
export const BUCKET_SECONDS: Record<string, number> = {
	'1min': 60,
	'5min': 300,
	'15min': 900,
	'1h': 3600,
	'6h': 6 * 3600,
	'12h': 12 * 3600,
	'24h': 86400,
}

/**
 * End of the bucket that starts at `startSec` = start of the next one.
 *
 * - 24h: the next local midnight (a DST day has 23 or 25 hours).
 * - 6h / 12h: on the local grid, so the bucket holding a DST switch is one hour
 *   shorter or longer — the offset change between start and end is added.
 * - up to 1h: a plain step; whole-hour offset changes do not move that grid.
 */
export const bucketEndSec = (startSec: number, timeKey: string): number => {
	if (timeKey === '24h') return dayjs.unix(startSec).add(1, 'day').unix()
	const size = BUCKET_SECONDS[timeKey] || 0
	if (size <= 3600) return startSec + size
	const end = startSec + size
	const shift = (new Date(end * 1000).getTimezoneOffset() - new Date(startSec * 1000).getTimezoneOffset()) * 60
	return end + shift
}

/** UI language as set by the language switcher (`<html lang>`); 'en' when unknown. */
export const uiLang = (): string => {
	try {
		const lang = document.documentElement.getAttribute('lang') || ''
		if (lang) return lang.slice(0, 2)
	} catch {
		// no document (tests / SSR)
	}
	return dayjs.locale() === 'de' ? 'de' : 'en'
}

/**
 * A bucket as two parts, e.g. `{ day: '20.10.', time: '06:00–12:00' }`, or for
 * 24h `{ day: 'Mo 20.10.', time: '' }`. An end at the following midnight reads
 * `24:00`, so the range stays on the day it belongs to.
 */
export const bucketRangeParts = (
	startSec: number,
	endSec: number,
	timeKey: string,
	withYear = false,
): { day: string; time: string } => {
	const lang = uiLang()
	const start = dayjs.unix(startSec).locale(lang)
	const dayFmt = withYear ? 'DD.MM.YYYY' : 'DD.MM.'
	if (timeKey === '24h') return { day: start.format(`dd ${dayFmt}`), time: '' }
	const end = dayjs.unix(endSec)
	const endTime = end.format('HH:mm') === '00:00' && !end.isSame(start, 'day') ? '24:00' : end.format('HH:mm')
	return { day: start.format(dayFmt), time: `${start.format('HH:mm')}–${endTime}` }
}

/** `20.10. 06:00–12:00` / `Mo 20.10.` */
export const formatBucketRange = (startSec: number, endSec: number, timeKey: string, withYear = false): string => {
	const { day, time } = bucketRangeParts(startSec, endSec, timeKey, withYear)
	return time ? `${day} ${time}` : day
}
